import { memo, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { outputSchemaPropTypes } from 'common/propTypes';
import _ from 'lodash';
import ErrorMesssage from 'v2/common/components/ErrorMessage/index';
import { signalTypes } from 'v2/modules/withRunForm/configs/index';
import { schemaShouldShowConfigTypes } from 'v2/common/quantConfig/outputSchema/config';
import { OutputSchemaUtility, TypeheadSchemaUtility } from 'v2/common/quantConfig/outputSchema/utility';
import CustomTypeAhead from 'v3/common/components/CustomTypeAhead';

const propTypes = {
  leftOperand: PropTypes.string,
  outputSchema: outputSchemaPropTypes.isRequired,
  onChangeLeftOperand: PropTypes.func.isRequired,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  signalType: PropTypes.string.isRequired,
  caseIndex: PropTypes.number,
};

const defaultProps = {
  leftOperand: '',
  caseIndex: undefined,
};

const LeftOperand = ({
  leftOperand,
  outputSchema,
  onChangeLeftOperand,
  control,
  errors,
  signalType,
  caseIndex,
}) => {
  const { exitSignal, entrySignal } = signalTypes;

  const validator = {
    validate: (value) => {
      if (_.isArray(value) && _.isEmpty(value[0]?.searchLabel)) return 'Please select a Left operand';

      return true;
    },
  };

  const typeaheadRef = useRef(null);

  const types = useMemo(() => {
    if (signalType === entrySignal) return [schemaShouldShowConfigTypes.entryLeftOperand];
    if (signalType === exitSignal) return [schemaShouldShowConfigTypes.exitLeftOperand];

    return [schemaShouldShowConfigTypes.adjustmentLeftOperand];
  }, [entrySignal, exitSignal, signalType]);

  const options = useMemo(() => {
    return _.chain(outputSchema)
      .thru((schema) => (signalType ? OutputSchemaUtility.filterByTypes(schema, types, caseIndex) : schema))
      .sortBy('priority')
      .thru((schema) => OutputSchemaUtility.filterByOnlyEnabled(schema))
      .map((schema) => TypeheadSchemaUtility.getOption(schema))
      .value();
  }, [outputSchema, types, signalType, caseIndex]);

  const uniqueId = useMemo(() => _.uniqueId('left-operand-'), []);
  const errorMessage = useMemo(
    () => (_.isEmpty(leftOperand) ? _.get(errors, `${uniqueId}.message`, '') : ''),
    [leftOperand, errors, uniqueId]
  );
  const selectedSchema = useMemo(
    () => OutputSchemaUtility.findByName(outputSchema, leftOperand),
    [outputSchema, leftOperand]
  );

  const selected = useMemo(
    () => TypeheadSchemaUtility.getSelectedOption(outputSchema, leftOperand),
    [outputSchema, leftOperand]
  );

  const handelLeftOperandChange = (onChange, value) => {
    const newValue = value || [];
    onChange(newValue);

    const operandId = _.isEmpty(newValue) ? null : _.first(newValue)?.id;
    onChangeLeftOperand(operandId);
  };

  useEffect(() => {
    if (typeaheadRef.current && _.isEmpty(selected)) {
      setTimeout(() => typeaheadRef.current.focus(), 0);
    }
  }, [selected]);
  return (
    <div className="left-operand operand-info d-flex align-items-center">
      <ErrorMesssage message={errorMessage}>
        <Controller
          render={({ onChange, name }) => {
            return (
              <CustomTypeAhead
                key={JSON.stringify(selected)}
                className="custom-form-control"
                disabled={false}
                ref={typeaheadRef}
                isInvalid={false}
                name={name}
                customKey={name}
                labelKey="searchLabel"
                options={options}
                clearButton={!_.isEmpty(selected)}
                defaultSelected={selected}
                filterBy={(option, filterProps) =>
                  TypeheadSchemaUtility.getOptionFilteredBySearchText(option, filterProps)
                }
                onChange={(value) => handelLeftOperandChange(onChange, value)}
              />
            );
          }}
          control={control}
          defaultValue={selected}
          name={uniqueId}
          rules={validator}
        />
        {selectedSchema?.infoLink && (
          <i
            className="fa-regular fa-circle-info info-icon ml-1 text-secondary"
            onClick={() => window.open(selectedSchema?.infoLink, '_blank')}
            style={{ cursor: 'pointer' }}
          ></i>
        )}
      </ErrorMesssage>
    </div>
  );
};

LeftOperand.propTypes = propTypes;
LeftOperand.defaultProps = defaultProps;

export default memo(LeftOperand);
